import { KindeContextProps } from '@kinde-oss/kinde-auth-react';
import type {
  Action,
  Agent,
  AgentRun,
  AgentRunEvent,
  Field,
  FieldConfig,
  JSONValue,
  Token,
} from '@lucidtech/las-sdk-browser';
import { Client, Credentials } from '@lucidtech/las-sdk-browser';
import moment from 'moment/moment';

import {
  EXPORT_TO_MAKE_FUNCTION_ID,
  EXPORT_TO_MICROSOFT_EXCEL_FUNCTION_ID,
  EXPORT_TO_N8N_FUNCTION_ID,
  EXPORT_TO_POWER_AUTOMATE_FUNCTION_ID,
  EXPORT_TO_WEBHOOK_FUNCTION_ID,
  EXPORT_TO_ZAPIER_FUNCTION_ID,
  EXTERNAL_ORG_ID_CLAIM,
  MULTI_VALUE_COLUMN_NAME,
} from '@/constants';
import { ActionState, AttentionBlock, FieldState, FormValue, FormValueRow, FormValues, Point } from '@/store';

import {
  Annotation,
  Annotations,
  FormatterWarningError,
  HeaderAnnotations,
  isArray,
  JSONType,
  TableAnnotations,
  ValidatorWarningError,
} from './types.ts';
import { DateRange } from 'react-aria-components';

export const createId = (resourceName: string, resourceId: number | string) => {
  return `cradl:${resourceName}:${resourceId}`;
};

export const generateId = () => {
  return (Math.random() + 1).toString(36).substring(2);
};

export const convertToState = (fieldConfig?: FieldConfig, parentId?: string) => {
  const fields: FieldState[] = [];
  const sortFn = (a: FieldState, b: FieldState) => (a.order ?? 0) - (b.order ?? 0);

  for (const [fieldId, config] of Object.entries(fieldConfig ?? {})) {
    const fieldState: FieldState = {
      id: fieldId,
      description: config.description,
      formatters: config.formatters,
      isNullable: config.isNullable,
      name: config.name,
      order: config.order,
      parentId,
      promptHint: config.promptHint,
      type: config.type,
      validators: config.validators,
    };

    if (config.type === 'table' && config.fields) {
      fieldState.fields = convertToState(config.fields, fieldState.id);
      fieldState.fields.sort(sortFn);
    }

    fields.push(fieldState);
  }

  fields.sort(sortFn);

  return fields;
};

export const convertToFieldConfig = (fields: FieldState[]) => {
  const fieldConfig: FieldConfig = {};

  for (const [index, field] of fields.entries()) {
    fieldConfig[field.id] = {
      description: field.description,
      formatters: field.formatters,
      isNullable: field.isNullable,
      name: field.name,
      order: index,
      promptHint: field.promptHint,
      type: field.type,
      validators: field.validators,
    };

    if (field.type === 'table' && field.fields) {
      fieldConfig[field.id].fields = convertToFieldConfig(field.fields);
    }

  }

  return fieldConfig;
};

export const listAllFields = (fieldConfig: FieldConfig) => {
  const fields: { name: string; field: Field }[] = [];

  for (const [fieldId, config] of Object.entries(fieldConfig)) {
    const fieldName = config.name ?? fieldId;
    fields.push({ name: fieldName, field: config });
  }

  return fields;
};

export const addUniqueValue = <T>(array: T[], value: T) => {
  if (array.indexOf(value) === -1) {
    return [...array, value];
  }
  return array;
};

export const removeUniqueValue = <T>(array: T[], value: T) => {
  const index = array.indexOf(value);
  if (index !== -1) {
    const newArray = [...array];
    newArray.splice(index, 1);
    return newArray;
  }
  return array;
};

export const attentionMapToBlocks = (attentionMap?: number[][]): AttentionBlock[] => {
  if (!attentionMap) return [];

  const blocks: AttentionBlock[] = [];
  for (const attention of attentionMap) {
    const copy = structuredClone(attention);
    const coords = copy.splice(1);
    const intensity = copy.pop() ?? 0.0;
    const xs = coords.filter((_, i) => i % 2 === 0);
    const ys = coords.filter((_, i) => i % 2 !== 0);
    const points: Point[] = [];
    for (let i = 0; i < Math.min(xs.length, ys.length); i++) {
      points.push({ x: xs[i], y: ys[i] });
    }
    blocks.push({ intensity, points });
  }
  return blocks;
};

export const capitalize = (str?: string | null) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatDate = (date?: Date) => {
  if (!date) return '';
  if (new Date().getTime() - date.getTime() < 60 * 1000) return 'Less than a minute ago';
  return capitalize(moment(date).fromNow());
};

export const formatDateFull = (date?: Date) => {
  if (!date) return '';
  return capitalize(moment(date).format('LLLL'));
};

export const formatDateShort = (date?: Date) => {
  if (!date) return '';
  return capitalize(moment(date).format('MMM DD'));
};

export const dateRangeToDates = (dateRange: DateRange, timezone: string) => {
  const now = new Date();

  const start = dateRange.start.toDate(timezone);
  start.setHours(now.getHours());
  start.setMinutes(now.getMinutes());
  start.setSeconds(now.getSeconds());

  const end = dateRange.end.toDate(timezone);
  end.setHours(now.getHours());
  end.setMinutes(now.getMinutes());
  end.setSeconds(now.getSeconds());

  return { start, end };
};

export const formatFileSize = (sizeInBytes: number) => {
  const sizeInKilobytes = sizeInBytes / 1000;
  if (sizeInKilobytes < 1) {
    return `${sizeInBytes.toPrecision(2)} B`;
  }
  const sizeInMegabytes = sizeInKilobytes / 1000;
  if (sizeInMegabytes < 1) {
    return `${sizeInKilobytes.toPrecision(2)} kB`;
  }
  const sizeInGigabytes = sizeInMegabytes / 1000;
  if (sizeInGigabytes < 1) {
    return `${sizeInMegabytes.toPrecision(2)} MB`;
  }
  return `${sizeInGigabytes.toPrecision(2)} GB`;
};

export const importJS = async (blob: Blob) => {
  const code = await blob.text();
  return import(
    /* @vite-ignore */ `data:text/javascript,${encodeURIComponent(code + '; export default CustomCode.main;')};`
  );
};

export const searchMatch = (value?: JSONType, search?: string) => {
  if (!search) return true;
  if (!value) return false;
  return JSON.stringify(value).toLowerCase().includes(search.toLowerCase());
};

export const objectEqual = (a: JSONType, b: JSONType) => {
  // TODO: Sort keys
  const aStr = JSON.stringify(a);
  const bStr = JSON.stringify(b);
  return aStr === bStr;
};

export const isDocumentId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:document:'));
};

export const isModelId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:model:'));
};

export const isActionId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:action:'));
};

export const isHookId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:hook:'));
};

export const isValidationId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:validation:'));
};

export const isPredictionId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:prediction:'));
};

export const isUserId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:user:'));
};

export const isValidationTaskId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:validation:') && id?.includes('/cradl:task:'));
};

export const isActionRunId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:action:') && id?.includes('/cradl:run:'));
};

export const isHookRunId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:hook:') && id?.includes('/cradl:run:'));
};

export const isAgentRunId = (id?: string | null): id is string => {
  return Boolean(id?.startsWith('cradl:agent:') && id?.includes('/cradl:run:'));
};

export function getResourceIds(resource?: Agent | null): string[];
export function getResourceIds(resource?: AgentRun | null): string[];
export function getResourceIds(resource?: Agent | AgentRun | null): string[] {
  if (!resource) return [];

  const resourceIds = [...resource.resourceIds];

  if ('events' in resource) {
    resourceIds.push(...resource.events.map((e) => e.resourceId));
  }

  return resourceIds;
}

export const findActionIds = (agent?: Agent | null) => {
  return getResourceIds(agent).filter(isActionId);
};

export const findValidationTaskId = (agentRun?: AgentRun | null) => {
  return getResourceIds(agentRun).findLast(isValidationTaskId);
};

export const findModelId = (agent?: Agent | null) => {
  return getResourceIds(agent).findLast(isModelId);
};

export const findValidationId = (agent?: Agent | null) => {
  return getResourceIds(agent).findLast(isValidationId);
};

export const findPredictionId = (agentRun?: AgentRun | null) => {
  return getResourceIds(agentRun).findLast(isPredictionId);
};

export const findDocumentId = (agentRun?: AgentRun | null) => {
  return getResourceIds(agentRun).findLast(isDocumentId);
};

export const fromActionRunId = (actionRunId?: string | null) => {
  if (!actionRunId || !actionRunId.includes('/')) {
    throw new Error(`Invalid action run id: ${actionRunId}`);
  }

  const [actionId, runId] = actionRunId.split('/');
  return { actionId, runId };
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const parseNumber = (value: any, predicate?: (value: number) => boolean) => {
  if (typeof value === 'string') value = Number.parseFloat(value);
  if (typeof value !== 'number') return;
  if (isNaN(value)) return;
  if (!predicate || predicate?.(value)) return value;
};

export const toDataUrl = (file: File | Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const toAnnotation = (formValue: FormValue): Annotation => {
  return {
    rawValue: formValue.current.rawValue ?? null,
    value: formValue.current.value ?? null,
    confidence: formValue.current.confidence ?? null,
    page: formValue.current.page ?? null,
    id: formValue.current.id ?? null,
  };
};

export const toAnnotations = (fieldConfig: FieldConfig, formValues: FormValues): Annotations => {
  const annotations: Annotations = {};

  for (const [id, config] of Object.entries(fieldConfig)) {
    const values = formValues[id];
    if (!values) continue;
    if (isArray(values)) {
      /* Convert table form values to line annotations for field */
      if (config.type === 'table') {
        const rows: TableAnnotations = [];
        for (const row of values) {
          const rowAnnotations: Annotations = {};
          for (const [subId, subValue] of Object.entries(row.columns)) {
            rowAnnotations[subId] = [toAnnotation(subValue)];
          }
          rows.push(rowAnnotations);
        }
        annotations[id] = rows;

        /* Convert table form values to multi value annotations for field */
      } else if (config.type === 'multi-value') {
        annotations[id] = values.map((v) => toAnnotation(v.columns[MULTI_VALUE_COLUMN_NAME]));
      }

      /* Convert form values to single value annotations for field */
    } else {
      annotations[id] = [toAnnotation(values)];
    }
  }

  return annotations;
};

const findAnnotations = (id: string, config: Field, ...annotationsArgs: Annotations[]) => {
  if (config.type === 'table') {
    const mergedAnnotations: TableAnnotations = [];

    for (const annotations of annotationsArgs) {
      const tableAnnotations = annotations[id] as TableAnnotations | undefined;

      for (const [i, row] of (tableAnnotations ?? []).entries()) {
        /* create new row in mergedAnnotations if it doesn't exist */
        if (!mergedAnnotations[i]) mergedAnnotations.push({});

        /* Iterate over columns in row */
        for (const [columnName, columnValues] of Object.entries(row)) {
          /* create new cell in mergedAnnotations if it doesn't exist */
          if (!mergedAnnotations[i][columnName]) mergedAnnotations[i][columnName] = [];

          for (const [j, annotation] of columnValues.entries()) {
            /* Get existing annotation or create new in mergedAnnotations */
            let existingAnnotation: Annotation;
            if (mergedAnnotations[i][columnName][j]) {
              existingAnnotation = mergedAnnotations[i][columnName][j];
            } else {
              existingAnnotation = {};
              mergedAnnotations[i][columnName].push(existingAnnotation);
            }

            for (const [key, value] of Object.entries(annotation)) {
              if (!(key in existingAnnotation)) existingAnnotation[key] = value;
            }
          }
        }
      }
    }

    return mergedAnnotations;
  } else {
    const mergedAnnotations: HeaderAnnotations = [];

    for (const annotations of annotationsArgs) {
      const headerAnnotations = annotations[id] as HeaderAnnotations | undefined;

      for (const [i, annotation] of (headerAnnotations ?? []).entries()) {
        /* Get existing annotation or create new in mergedAnnotations */
        let existingAnnotation: Annotation;
        if (mergedAnnotations[i]) {
          existingAnnotation = mergedAnnotations[i];
        } else {
          existingAnnotation = {};
          mergedAnnotations.push(existingAnnotation);
        }

        for (const [key, value] of Object.entries(annotation)) {
          if (!(key in existingAnnotation)) existingAnnotation[key] = value;
        }
      }
    }

    return mergedAnnotations;
  }
};

const createWarningsAndErrors = (
  formatters?: FormatterWarningError[] | null,
  validators?: ValidatorWarningError[] | null
) => {
  const metaErrors = [];

  const meta: Record<string, any> = {
    isBlurred: false,
    isDirty: false,
    isPristine: true,
    isTouched: false,
    isValidating: false,
    formatters: formatters ?? [],
    validators: validators ?? [],
  };

  for (const formatter of formatters ?? []) {
    if (formatter.errors.length > 0) metaErrors.push({ id: formatter.id, errors: formatter.errors });
  }

  for (const validator of validators ?? []) {
    if (validator.errors.length > 0) metaErrors.push({ id: validator.id, errors: validator.errors });
  }

  if (metaErrors.length > 0) meta.errorMap = { onMount: metaErrors };

  return meta;
};

export const toFormValues = (fieldConfig: FieldConfig, ...annotationsArgs: Annotations[]): FormValues => {
  const formValues: FormValues = {};
  const formMeta: Record<string, any> = {};
  const createDefaultValue = () => ({ current: { value: '' }, candidates: [] });

  for (const [id, config] of Object.entries(fieldConfig)) {
    const values = structuredClone(findAnnotations(id, config, ...annotationsArgs));

    /* Cannot find any annotations for field */
    if (!values) {
      formValues[id] = config.type === 'table' || config.type === 'multi-value' ? [] : createDefaultValue();
      continue;
    }

    /* Convert line annotations to table form values for field */
    if (config.type === 'table') {
      if (!config.fields) continue;
      const rowValues: FormValueRow[] = [];

      for (const [i, row] of (values as TableAnnotations).entries()) {
        const rowId = generateId();
        const rowValue: FormValueRow = { id: rowId, columns: {} };

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (const [subId, _] of Object.entries(config.fields)) {
          const value = row[subId];

          if (value && value.length > 0) {
            const current = value[0];

            rowValue.columns[subId] = {
              current: { ...current, isValidated: true },
              candidates: value,
            };
            const metaId = `${id}[${i}].columns.${subId}`;
            formMeta[metaId] = createWarningsAndErrors(current.formatters, current.validators);
          } else {
            rowValue.columns[subId] = createDefaultValue();
          }
        }
        rowValues.push(rowValue);
      }

      formValues[id] = rowValues;

      /* Convert multi value annotations to table form values for field */
    } else if (config.type === 'multi-value') {
      const multiValues: FormValueRow[] = [];

      for (const [i, multiValue] of (values as HeaderAnnotations).entries()) {
        const multiId = generateId();

        multiValues.push({
          id: multiId,
          columns: {
            [MULTI_VALUE_COLUMN_NAME]: {
              current: { ...multiValue, isValidated: true },
              candidates: [multiValue],
            },
          },
        });

        const metaId = `${id}[${i}].columns.${MULTI_VALUE_COLUMN_NAME}`;
        formMeta[metaId] = createWarningsAndErrors(multiValue.formatters, multiValue.validators);
      }

      formValues[id] = multiValues;

      /* Convert single value annotations to form values for field */
    } else {
      const value = values as HeaderAnnotations;

      if (value.length > 0) {
        const current = value[0];
        formValues[id] = {
          current: { ...current, isValidated: true },
          candidates: value,
        };
        formMeta[id] = createWarningsAndErrors(current.formatters, current.validators);
      } else {
        formValues[id] = createDefaultValue();
      }
    }
  }

  return { values: formValues, meta: formMeta };
};

export const getScopes = (jwt?: JSONType) => {
  const scopes = jwt?.scope || '';
  if (typeof scopes !== 'string') {
    return [];
  } else {
    return scopes.split(' ').map((scope) => (scope.includes('/') ? scope.split('/')[1] : scope)) || [];
  }
};

export const jsonToList = <T extends object>(obj?: JSONValue): T[] => {
  if (!obj) return [];

  if (Array.isArray(obj)) {
    return obj as T[];
  }

  throw new Error('Object is not a list');
};

export const createLocalStorageKey = (key: string) => {
  return `cradl:${key}`;
};

const isExcelConfigured = (action: Action | ActionState) => {
  if (action.config?.createNewWorkbook) {
    return action.config.driveItemId && action.config.driveId;
  } else {
    return (
      action.config.driveItemId && action.config.driveId && action.config.tableId && action.config.fieldToColumnMap
    );
  }
};

export const isNotConfiguredExport = (action?: Action | ActionState | null) => {
  if (!action) return false;

  switch (action.functionId) {
    case EXPORT_TO_MICROSOFT_EXCEL_FUNCTION_ID:
      return !isExcelConfigured(action);
    case EXPORT_TO_POWER_AUTOMATE_FUNCTION_ID:
      return !action.enabled || (!action.config.waitForResult && !action.config.url);
    case EXPORT_TO_ZAPIER_FUNCTION_ID:
      return !action.enabled || !action.config.url;
    case EXPORT_TO_MAKE_FUNCTION_ID:
    case EXPORT_TO_WEBHOOK_FUNCTION_ID:
      return !action.config.url;
    case EXPORT_TO_N8N_FUNCTION_ID:
      return !action.enabled || !action.config.url || !action.config.nodeId;
  }

  return false;
};

export const removeKeys = (obj: object, excludedKeys: string[]): JSONValue => {
  if (Array.isArray(obj)) {
    return obj.map((item) => removeKeys(item, excludedKeys));
  }

  if (typeof obj === 'object' && obj !== null) {
    const sanitized: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (!excludedKeys.includes(key)) {
        sanitized[key] = removeKeys(value, excludedKeys);
      }
    }

    return sanitized;
  }

  return obj;
};

export class TokenCredentials extends Credentials {
  readonly organizationId?: string | null;
  protected auth: KindeContextProps;

  constructor(apiEndpoint: string, auth: KindeContextProps, organizationId?: string | null) {
    super(apiEndpoint, undefined);
    this.auth = auth;
    this.organizationId = organizationId;
  }

  getAccessToken(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.auth
        .getAccessToken()
        .then((token) => {
          if (token) {
            resolve(token);
          } else {
            reject(new Error('No access token available'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  protected getToken(): Promise<Token> {
    return new Promise<Token>((_, reject) => {
      reject(new Error('TokenCredentials does not support getToken, use getAccessToken instead'));
    });
  }
}

export const createClient = (auth: KindeContextProps, organizationId?: string | null) => {
  return new Client(new TokenCredentials(import.meta.env.VITE_API_ENDPOINT, auth, organizationId));
};

export const getErrors = (fieldMeta?: Record<string, any>) => {
  const all = [...(fieldMeta?.formatters ?? []), ...(fieldMeta?.validators ?? [])];
  return all.filter((e) => e.errors.length > 0);
};

export const getWarnings = (fieldMeta?: Record<string, any>) => {
  const all = [...(fieldMeta?.formatters ?? []), ...(fieldMeta?.validators ?? [])];
  return all.filter((e) => e.warnings.length > 0);
};

export const createClientFromAuth = async (auth: KindeContextProps) => {
  const claim = await auth.getClaim(EXTERNAL_ORG_ID_CLAIM);
  return createClient(auth, claim?.value as string | undefined | null);
};

export const isEmpty = (d?: object) => {
  return !d || Object.keys(d).length === 0;
};

export const uniqueBy = <T>(items: T[], fn: (lhs: T, rhs: T) => boolean) => {
  return items.filter((value, idx, self) => self.findIndex((v) => fn(v, value)) === idx);
};

export const safeDiv = (a?: number | null, b?: number | null) => {
  if (!a || !b) return 0;
  return a / b;
};

type FilterEventsFromLastRunArgs = {
  predictionEvents: AgentRunEvent[];
  reviewEvents: AgentRunEvent[];
  exportEvents: AgentRunEvent[];
};

export const filterEventsFromLastRun = ({
  exportEvents,
  predictionEvents,
  reviewEvents,
}: FilterEventsFromLastRunArgs) => {
  let lastPredictionEvents = [...predictionEvents];
  let lastReviewEvents = [...reviewEvents];
  let lastExportEvents = [...exportEvents];

  if (predictionEvents.length === 0) {
    return { lastPredictionEvents, lastReviewEvents, lastExportEvents };
  }

  const cutoffTimestamp = predictionEvents[predictionEvents.length - 1].timestamp;

  /* Prediction events */
  lastPredictionEvents = lastPredictionEvents.filter((e) => e.timestamp >= cutoffTimestamp);

  /* Review events */
  lastReviewEvents = lastReviewEvents.filter((e) => e.timestamp >= cutoffTimestamp);

  const uniqueValidationId = (events: AgentRunEvent[], currentEvent: AgentRunEvent) => {
    const exists = !!events.find((e) => e.validationId === currentEvent.validationId);
    if (!exists) events.push(currentEvent);
    return events;
  };

  lastReviewEvents = lastReviewEvents.reduceRight(uniqueValidationId, []);
  lastReviewEvents.reverse();

  /* Export events */
  lastExportEvents = lastExportEvents.filter((e) => e.timestamp >= cutoffTimestamp);

  const uniqueActionId = (events: AgentRunEvent[], currentEvent: AgentRunEvent) => {
    const exists = !!events.find((e) => e.actionId === currentEvent.actionId);
    if (!exists) events.push(currentEvent);
    return events;
  };

  lastExportEvents = lastExportEvents.reduceRight(uniqueActionId, []);
  lastExportEvents.reverse();

  return { lastPredictionEvents, lastReviewEvents, lastExportEvents };
};
